import { z } from 'zod';

/**
 * Outbound mail: the operator's SMTP settings, and the record of what was sent.
 *
 * The platform ships with mail OFF and works perfectly that way — that is the
 * state every install starts in and most stay in. Nothing here is required for
 * anything else to function; when no host is configured the send service is a
 * no-op that says so, and an invoice is exactly as valid as it was before.
 *
 * The password is never in any of these shapes. It goes in through
 * `updateMailSettingsSchema` and comes back only as `passwordConfigured` —
 * the same split the map provider key and the OLT credentials use.
 */

/**
 * How the connection is encrypted. Three values because there are three real
 * deployments, not because a boolean was insufficient:
 *
 *   tls       implicit TLS from the first byte — port 465, what most hosted
 *             providers want.
 *   starttls  plaintext connect, then upgrade before anything secret is sent —
 *             port 587, the other half of the world. REQUIRED, not optional:
 *             a server that does not offer it is refused rather than silently
 *             downgraded, because silently downgrading is how a password ends
 *             up on the wire.
 *   none      no encryption at all. For a relay on this machine or on the
 *             management LAN; the send service refuses to authenticate over
 *             it unless the host is loopback.
 */
export const MAIL_ENCRYPTIONS = ['tls', 'starttls', 'none'] as const;
export type MailEncryption = (typeof MAIL_ENCRYPTIONS)[number];

/**
 * What each delivery record is FOR — the answer to "what mail is this?".
 *
 * The last two are the first messages this platform sends to a PAYER rather
 * than to an org admin: an operator hands one bill, or one receipt, to the
 * person who actually settles it. They are their own kinds and not
 * `invoice_issued` with a different body, because "the platform told the
 * tenant about a bill" and "a named human was sent a link to it" are different
 * events, and the record is the thing that gets read back when somebody asks
 * which happened.
 */
export const MAIL_KINDS = [
  'test',
  'invoice_issued',
  'payment_receipt',
  'invoice_link',
  'receipt_link',
  /**
   * The three account-security messages. Their own kinds because they are the
   * first mail this platform sends that is part of an AUTHENTICATION flow, and
   * "was a code actually delivered to this person" is a question an incident
   * asks by name.
   *
   *   contact_code    a six-digit code sent to an address being proved
   *   contact_notice  the warning to the OLD address that a change was asked
   *                   for — the message that makes a stolen session survivable
   *   mfa_code        a sign-in code for somebody who has 2FA switched on
   */
  'contact_code',
  'contact_notice',
  'mfa_code',
  /**
   * The sign-in address and username for an administrator the onboarding
   * wizard just created (2026-09-17) — the password too, only when the
   * operator ticked that.
   */
  'welcome',
] as const;
export type MailKind = (typeof MAIL_KINDS)[number];

/**
 * Where one message got to.
 *
 *   queued   accepted by the send service, not yet handed to a server
 *   sending  a worker has it right now
 *   sent     an SMTP server accepted it — the strongest claim mail can make
 *   failed   every attempt was refused or timed out; `error` says how
 *   skipped  never attempted, and why: mail is switched off, or nobody on the
 *            organization had an address to send to. A recorded non-event, so
 *            "did that invoice go out" is answerable with "no, because …"
 *            rather than with silence.
 */
export const MAIL_DELIVERY_STATUSES = ['queued', 'sending', 'sent', 'failed', 'skipped'] as const;
export type MailDeliveryStatus = (typeof MAIL_DELIVERY_STATUSES)[number];

export const mailSettingsSchema = z.object({
  host: z.string(),
  port: z.number().int(),
  encryption: z.enum(MAIL_ENCRYPTIONS),
  username: z.string(),
  /** Never the password itself — only whether one is saved. */
  passwordConfigured: z.boolean(),
  fromName: z.string(),
  fromAddress: z.string(),
  replyTo: z.string(),
  /**
   * Whether mail can actually be sent right now: a host and a from-address.
   * Everything that sends checks this one flag, so "configured" means the
   * same thing on the settings page as it does in the send service.
   */
  configured: z.boolean(),
});
export type MailSettings = z.infer<typeof mailSettingsSchema>;

export const updateMailSettingsSchema = z.object({
  host: z.string().max(255).optional(),
  port: z.coerce.number().int().min(1).max(65535).optional(),
  encryption: z.enum(MAIL_ENCRYPTIONS).optional(),
  username: z.string().max(255).optional(),
  /** Empty string is ignored — an untouched password field must not erase one. */
  password: z.string().max(255).optional(),
  /** The explicit erase, because "" cannot be it. */
  clearPassword: z.boolean().optional(),
  fromName: z.string().max(120).optional(),
  fromAddress: z.string().max(255).optional(),
  replyTo: z.string().max(255).optional(),
});
export type UpdateMailSettings = z.infer<typeof updateMailSettingsSchema>;

export const sendTestMailSchema = z.object({
  /** Defaults to the caller's own address, which is the honest default. */
  to: z.string().email().max(255).optional(),
});

/**
 * One attempt at one message, as the operator sees it. No body: the record
 * exists to answer "did it go out", not to keep a copy of the mail.
 */
export const mailDeliverySchema = z.object({
  id: z.string(),
  kind: z.enum(MAIL_KINDS),
  status: z.enum(MAIL_DELIVERY_STATUSES),
  toAddress: z.string(),
  subject: z.string(),
  orgId: z.string().nullable(),
  orgName: z.string().nullable(),
  /** What the message is about — `invoice`/`payment` and its id. */
  relatedType: z.string().nullable(),
  relatedId: z.string().nullable(),
  attempts: z.number().int(),
  /** Why it failed or was skipped, in words an operator can act on. */
  error: z.string().nullable(),
  createdAt: z.number().int(),
  sentAt: z.number().int().nullable(),
});
export type MailDelivery = z.infer<typeof mailDeliverySchema>;

/** What `POST /api/email/test` answers: it either reached a server, or it did not. */
export const mailTestResultSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
  /** Round trip to the SMTP server, so a slow relay is visible as slow. */
  ms: z.number().int(),
  /** The delivery record this test wrote, so it appears in the same list. */
  deliveryId: z.string().nullable(),
});
export type MailTestResult = z.infer<typeof mailTestResultSchema>;
